import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import { Field } from 'react-final-form';
import { serverApiUrl } from '../../../config/constants';
import httpClient from '../../../core/httpClient';

const ImageUploadField = ({ name: fieldName, label, folder = 'word-finder/', helperText }) => (
  <Field name={fieldName}>
    {({ input }) => (
      <ImageUploadFieldInner
        value={input.value}
        onChange={input.onChange}
        label={label}
        folder={folder}
        fieldName={fieldName}
        helperText={helperText}
      />
    )}
  </Field>
);

const ImageUploadFieldInner = ({ value, onChange, label, folder, fieldName, helperText }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (value) {
      // If value is a full URL, use it directly
      if (value.startsWith('http')) {
        setPreviewUrl(value);
      } else {
        // Otherwise, try to get the URL from the server
        const url = `${serverApiUrl}/services/file?file_key=${value}`;
        httpClient(url, { method: 'GET' })
          .then((res) => {
            setPreviewUrl(res.json.url);
          })
          .catch(() => {
            setPreviewUrl(null);
          });
      }
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const url = `${serverApiUrl}/admin/word-finder-config/upload`;
      const response = await httpClient(url, {
        method: 'POST',
        body: formData,
      });

      if (response.json.url) {
        onChange(response.json.url);
        setPreviewUrl(response.json.url);
      }
    } catch (err) {
      setError('Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setPreviewUrl(null);
  };

  return (
    <Box mt={2} mb={2}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      {helperText && (
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
          {helperText}
        </Typography>
      )}

      <Box display="flex" alignItems="flex-start" gap={2}>
        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt={label}
            sx={{
              maxWidth: 200,
              maxHeight: 150,
              objectFit: 'contain',
              border: '1px solid #ddd',
              borderRadius: 4,
            }}
            style={{
              maxWidth: 200,
              maxHeight: 150,
              objectFit: 'contain',
              border: '1px solid #ddd',
              borderRadius: 4,
            }}
          />
        )}

        <Box>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id={`image-upload-${fieldName}`}
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor={`image-upload-${fieldName}`}>
            <Button
              variant="outlined"
              component="span"
              disabled={uploading}
              size="small"
            >
              {uploading ? <CircularProgress size={20} /> : 'Upload Image'}
            </Button>
          </label>

          {value && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              size="small"
              style={{ marginLeft: 8 }}
            >
              Clear
            </Button>
          )}

          {error && (
            <Typography variant="caption" color="error" display="block" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          )}

          {value && (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
              {value.length > 50 ? `...${value.slice(-50)}` : value}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ImageUploadField;
